/**
 * Smart Cache Implementation
 *
 * Provides intelligent caching with TTL, automatic invalidation, and performance optimization.
 * Supports different cache strategies and memory management.
 */
/**
 * Smart Cache Class
 */
export class SmartCache {
    constructor(options = {}) {
        this.cache = new Map();
        this.options = {
            maxSize: options.maxSize || 1000,
            maxMemory: options.maxMemory || 50 * 1024 * 1024, // 50MB default
            defaultTtl: options.defaultTtl || 5 * 60 * 1000, // 5 minutes default
            cleanupInterval: options.cleanupInterval || 60 * 1000, // 1 minute
            enableStats: options.enableStats ?? true
        };
        this.stats = {
            hits: 0,
            misses: 0,
            sets: 0,
            deletes: 0,
            evictions: 0,
            size: 0,
            memoryUsage: 0,
            hitRate: 0
        };
        this.startCleanupTimer();
    }
    /**
     * Set a value in the cache
     */
    set(key, data, ttl, metadata) {
        const itemTtl = ttl || this.options.defaultTtl;
        const item = {
            data,
            timestamp: Date.now(),
            ttl: itemTtl,
            accessCount: 0,
            lastAccessed: Date.now(),
            metadata: {
                key,
                size: this.estimateSize(data),
                tags: metadata?.tags || [],
                priority: metadata?.priority || 'medium'
            }
        };
        // Check if we need to evict items before adding
        this.ensureCapacity();
        this.cache.set(key, item);
        this.stats.sets++;
        this.updateStats();
    }
    /**
     * Get a value from the cache
     */
    get(key) {
        const item = this.cache.get(key);
        if (!item) {
            this.stats.misses++;
            this.updateStats();
            return null;
        }
        // Check if item has expired
        if (this.isExpired(item)) {
            this.delete(key);
            this.stats.misses++;
            this.updateStats();
            return null;
        }
        // Update access statistics
        item.accessCount++;
        item.lastAccessed = Date.now();
        this.stats.hits++;
        this.updateStats();
        return item.data;
    }
    /**
     * Check if a key exists and is not expired
     */
    has(key) {
        const item = this.cache.get(key);
        return item ? !this.isExpired(item) : false;
    }
    /**
     * Delete a key from the cache
     */
    delete(key) {
        const deleted = this.cache.delete(key);
        if (deleted) {
            this.stats.deletes++;
            this.updateStats();
        }
        return deleted;
    }
    /**
     * Clear all items from the cache
     */
    clear() {
        this.cache.clear();
        this.resetStats();
    }
    /**
     * Get cache statistics
     */
    getStats() {
        return { ...this.stats };
    }
    /**
     * Get all keys in the cache
     */
    keys() {
        return Array.from(this.cache.keys());
    }
    /**
     * Get cache size
     */
    size() {
        return this.cache.size;
    }
    /**
     * Invalidate items by tag
     */
    invalidateByTag(tag) {
        let invalidated = 0;
        for (const [key, item] of this.cache.entries()) {
            if (item.metadata?.tags?.includes(tag)) {
                this.cache.delete(key);
                invalidated++;
            }
        }
        this.stats.deletes += invalidated;
        this.updateStats();
        return invalidated;
    }
    /**
     * Invalidate items by pattern
     */
    invalidateByPattern(pattern) {
        let invalidated = 0;
        for (const key of this.cache.keys()) {
            if (pattern.test(key)) {
                this.cache.delete(key);
                invalidated++;
            }
        }
        this.stats.deletes += invalidated;
        this.updateStats();
        return invalidated;
    }
    /**
     * Get items by priority
     */
    getByPriority(priority) {
        const items = [];
        for (const [key, item] of this.cache.entries()) {
            if (item.metadata?.priority === priority) {
                items.push({ key, item });
            }
        }
        return items;
    }
    /**
     * Get least recently used items
     */
    getLRUItems(count = 10) {
        const items = Array.from(this.cache.entries())
            .map(([key, item]) => ({ key, item }))
            .sort((a, b) => a.item.lastAccessed - b.item.lastAccessed)
            .slice(0, count);
        return items;
    }
    /**
     * Get most frequently accessed items
     */
    getMFUItems(count = 10) {
        const items = Array.from(this.cache.entries())
            .map(([key, item]) => ({ key, item }))
            .sort((a, b) => b.item.accessCount - a.item.accessCount)
            .slice(0, count);
        return items;
    }
    /**
     * Check if an item is expired
     */
    isExpired(item) {
        return Date.now() - item.timestamp > item.ttl;
    }
    /**
     * Estimate the size of data in bytes
     */
    estimateSize(data) {
        try {
            return new Blob([JSON.stringify(data)]).size;
        }
        catch {
            return 0;
        }
    }
    /**
     * Ensure cache capacity by evicting items if necessary
     */
    ensureCapacity() {
        // Remove expired items first
        this.removeExpiredItems();
        // Check size limits
        if (this.cache.size >= this.options.maxSize) {
            this.evictItems(this.cache.size - this.options.maxSize + 1);
        }
        // Check memory limits
        const currentMemory = this.calculateMemoryUsage();
        if (currentMemory > this.options.maxMemory) {
            this.evictByMemory(currentMemory - this.options.maxMemory);
        }
    }
    /**
     * Remove expired items from cache
     */
    removeExpiredItems() {
        const expiredKeys = [];
        for (const [key, item] of this.cache.entries()) {
            if (this.isExpired(item)) {
                expiredKeys.push(key);
            }
        }
        expiredKeys.forEach(key => this.cache.delete(key));
    }
    /**
     * Evict items using LRU strategy
     */
    evictItems(count) {
        const lruItems = this.getLRUItems(count);
        lruItems.forEach(({ key }) => {
            this.cache.delete(key);
            this.stats.evictions++;
        });
    }
    /**
     * Evict items based on memory usage
     */
    evictByMemory(targetMemoryReduction) {
        let freedMemory = 0;
        const lruItems = this.getLRUItems();
        for (const { key, item } of lruItems) {
            if (freedMemory >= targetMemoryReduction)
                break;
            const itemSize = item.metadata?.size || 0;
            this.cache.delete(key);
            freedMemory += itemSize;
            this.stats.evictions++;
        }
    }
    /**
     * Calculate current memory usage
     */
    calculateMemoryUsage() {
        let totalSize = 0;
        for (const item of this.cache.values()) {
            totalSize += item.metadata?.size || 0;
        }
        return totalSize;
    }
    /**
     * Update cache statistics
     */
    updateStats() {
        if (!this.options.enableStats)
            return;
        this.stats.size = this.cache.size;
        this.stats.memoryUsage = this.calculateMemoryUsage();
        this.stats.hitRate = this.stats.hits / (this.stats.hits + this.stats.misses) || 0;
    }
    /**
     * Reset cache statistics
     */
    resetStats() {
        this.stats = {
            hits: 0,
            misses: 0,
            sets: 0,
            deletes: 0,
            evictions: 0,
            size: 0,
            memoryUsage: 0,
            hitRate: 0
        };
    }
    /**
     * Start cleanup timer
     */
    startCleanupTimer() {
        this.cleanupTimer = setInterval(() => {
            this.removeExpiredItems();
        }, this.options.cleanupInterval);
    }
    /**
     * Stop cleanup timer
     */
    stop() {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
        }
    }
    /**
     * Destroy cache and cleanup resources
     */
    destroy() {
        this.stop();
        this.clear();
    }
}
/**
 * Global cache instance
 */
export const globalCache = new SmartCache({
    maxSize: 1000,
    maxMemory: 50 * 1024 * 1024, // 50MB
    defaultTtl: 5 * 60 * 1000, // 5 minutes
    cleanupInterval: 60 * 1000, // 1 minute
    enableStats: true
});
/**
 * Cache factory for creating specialized caches
 */
export class CacheFactory {
    static createLessonCache() {
        return new SmartCache({
            maxSize: 500,
            defaultTtl: 10 * 60 * 1000, // 10 minutes for lessons
            enableStats: true
        });
    }
    static createNoteCache() {
        return new SmartCache({
            maxSize: 1000,
            defaultTtl: 2 * 60 * 1000, // 2 minutes for notes
            enableStats: true
        });
    }
    static createUserCache() {
        return new SmartCache({
            maxSize: 100,
            defaultTtl: 30 * 60 * 1000, // 30 minutes for user data
            enableStats: true
        });
    }
    static createSessionCache() {
        return new SmartCache({
            maxSize: 50,
            defaultTtl: 60 * 60 * 1000, // 1 hour for session data
            enableStats: false
        });
    }
}
/**
 * Cache decorator for functions
 */
export function withCache(fn, cache, keyGenerator) {
    return ((...args) => {
        const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);
        const cached = cache.get(key);
        if (cached !== null) {
            return cached;
        }
        const result = fn(...args);
        // Handle promises
        if (result instanceof Promise) {
            return result.then(value => {
                cache.set(key, value);
                return value;
            });
        }
        cache.set(key, result);
        return result;
    });
}
/**
 * Cache hook for React components
 */
export const useCache = () => {
    return {
        get: globalCache.get.bind(globalCache),
        set: globalCache.set.bind(globalCache),
        has: globalCache.has.bind(globalCache),
        delete: globalCache.delete.bind(globalCache),
        clear: globalCache.clear.bind(globalCache),
        getStats: globalCache.getStats.bind(globalCache),
        invalidateByTag: globalCache.invalidateByTag.bind(globalCache),
        invalidateByPattern: globalCache.invalidateByPattern.bind(globalCache)
    };
};
